import React, { useEffect, useState } from "react";
import { X, Ticket, Newspaper, Calendar, ExternalLink } from "lucide-react";
import { api } from "../../lib/api";

/**
 * PromoBanner — hero card that fires at the top of the homepage when a
 * newspaper partner promo is imminent or live.
 *
 * Behaviour per user brief:
 *   • Only appears leading up to (from `preview_from`, default T-3d) and
 *     during a promo window — hidden otherwise.
 *   • Lists every participating newspaper with its own logo.
 *   • Countdown text carries the urgency ("Starts in 2 days" → "3 days
 *     left to grab yours").
 *   • Dismissable per promo_id in localStorage so returning users don't
 *     get nagged, but a *new* promo replays.
 */
export default function PromoBanner() {
  const [promos, setPromos] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cc_promo_dismissed") || "[]"); }
    catch { return []; }
  });

  useEffect(() => {
    let alive = true;
    api.get("/promos/active").then(({ data }) => {
      if (alive) setPromos(data.promos || []);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const visible = promos.filter((p) => !dismissedIds.includes(p.promo_id));
  if (visible.length === 0) return null;
  const promo = visible[0];  // one at a time — biggest one first

  const dismiss = () => {
    const next = [...dismissedIds, promo.promo_id];
    setDismissedIds(next);
    try { localStorage.setItem("cc_promo_dismissed", JSON.stringify(next)); } catch { /* ignore */ }
  };

  const isLive = promo.is_active_now;
  const urgency = isLive
    ? `${promo.days_until_end} day${promo.days_until_end === 1 ? "" : "s"} left to grab yours`
    : promo.days_until_start === 0
      ? "Live today!"
      : `Starts in ${promo.days_until_start} day${promo.days_until_start === 1 ? "" : "s"}`;

  return (
    <div className="w-full bg-gradient-to-br from-[#0a7f3f] via-[#116e3f] to-[#0e5d33] text-white" data-testid="promo-banner-hero">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 relative">
        <button
          type="button"
          onClick={dismiss}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 text-white/70 hover:text-white p-1.5"
          data-testid="promo-banner-dismiss"
          aria-label="Dismiss promo banner"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3 sm:gap-4">
          <div className="bg-white/15 backdrop-blur-sm rounded-full p-2.5 shrink-0" data-testid="promo-banner-icon">
            <Ticket className="w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-2 bg-[#ff4500] px-2.5 py-0.5 text-[10px] mono uppercase tracking-widest font-black mb-2">
              <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
              {urgency}
            </div>
            <h2 className="font-display text-2xl sm:text-3xl leading-tight mb-1" data-testid="promo-banner-title">
              {promo.title}
            </h2>
            {promo.subtitle && (
              <p className="text-white/90 text-sm sm:text-base leading-snug mb-3" data-testid="promo-banner-subtitle">
                {promo.subtitle}
              </p>
            )}

            {promo.retailers?.length > 0 && (
              <div className="mb-3">
                <div className="text-[10px] mono uppercase tracking-widest text-white/70 mb-1.5 flex items-center gap-1.5">
                  <Newspaper className="w-3 h-3" /> Available in
                </div>
                <div className="flex flex-wrap gap-2" data-testid="promo-banner-retailers">
                  {promo.retailers.map((r, i) => (
                    <RetailerChip key={i} retailer={r} />
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm">
              <div className="inline-flex items-center gap-1.5 text-white/85">
                <Calendar className="w-3.5 h-3.5" />
                {promo.valid_from} → {promo.valid_to}
              </div>
              {promo.redemption_url && (
                <a
                  href={promo.redemption_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 bg-white text-[#0a7f3f] font-black text-[11px] sm:text-xs uppercase tracking-widest px-3.5 py-2 hover:bg-[#fff8ec] transition-colors"
                  data-testid="promo-banner-cta"
                >
                  How to claim <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>

            {promo.entitlement && (
              <p className="text-white/70 text-[11px] mt-2.5 italic" data-testid="promo-banner-entitlement">
                {promo.entitlement}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Small retailer chip with its own favicon + purchase hint. */
function RetailerChip({ retailer }) {
  const [failed, setFailed] = useState(false);
  const logo = retailer.logo_domain
    ? `https://www.google.com/s2/favicons?domain=${retailer.logo_domain}&sz=64`
    : null;
  return (
    <span
      className="inline-flex items-center gap-1.5 bg-white/95 text-[#0e0e0e] pr-2.5 pl-1 py-1 text-[11px] font-semibold rounded-sm"
      title={retailer.purchase_hint || retailer.name}
    >
      {logo && !failed ? (
        <img
          src={logo}
          alt=""
          className="w-4 h-4 object-contain shrink-0"
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      ) : (
        <Newspaper className="w-3.5 h-3.5 text-[#666]" />
      )}
      {retailer.name}
      {retailer.purchase_hint && (
        <span className="text-[#777] font-normal">· {retailer.purchase_hint}</span>
      )}
    </span>
  );
}
